/**
 * @file Header quick-add entry point for Recalls.
 */

import { openStandardForm } from "../../shared/forms/standardFormModal.js";
import { closeForm } from "../../shared/forms/modalShell.js";
import { appData, state } from "../../app/state/state.js";
import {
    addOptimisticRecallDraft,
    removeCachedRecallDraft,
    replaceOptimisticRecallDraft,
    upsertCachedRecallDraft,
} from "./recallsState.js";
import {
    bindNativeFieldBehavior,
    FIXED_TEXTAREA_CLASSES,
    getNativeFieldWrapperHtml,
    NATIVE_INPUT_CLASSES,
} from "../../shared/forms/nativeFieldStyles.js";
import { createDateControl } from "../../shared/ui/controls/dateControl.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import { openConfirmationModal } from "../../shared/ui/confirmationModal.js";
import { initTagging } from "../../shared/ui/tagging.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";
import {
    deleteRecallDraft,
    promoteRecallDraft,
    saveRecallDraft,
} from "./recallsApi.js";
import {
    collectRecallEntryData,
    confirmNearDateIfNeeded,
    handleRecallWarningResponse,
    isQuickAddSaveReady,
    saveRecallQuickAdd,
} from "./recallsEntry.js";
import {
    RECALLS_MODAL_BODY_SPACING_CLASSES,
    RECALLS_MODAL_GRID_CLASSES,
    setRecallsModalActionProcessing,
} from "./recallsModalLayout.js";
import {
    normalizeRecallReasonIds,
    serializeRecallReasonIds,
} from "./recallsReasonUtils.js";
import { bindRecallPhoneInputSanitizer } from "./recallsPhoneValidation.js";
import {
    createRecallProviderSelectPreset,
    getCurrentUserRecallProviderDefault,
    getRecallProviderControlValues,
    parseRecallProviderMirrorValue,
} from "./recallsProviderUtils.js";
import {
    openRecallsConfigIncompleteModal,
} from "./recallsConfigIncomplete.js";
import {
    refreshRecallsAfterPermanentSave,
    refreshRecallsAfterDraftPromotion,
} from "./recallsPromotionUi.js";

function attrValue(value) {
    return escapeHTML(String(value ?? ""))
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function showErrorModal(title, message) {
    openConfirmationModal(
        title,
        escapeHTML(message),
        async () => {},
        false,
        "OK",
    );
}

function createOptimisticDraftId() {
    return `optimistic-recall-draft-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}`;
}

function buildOptimisticDraft(data = {}, draft = {}) {
    return {
        ...draft,
        ...data,
        id: draft.id || createOptimisticDraftId(),
        __optimisticDraft: true,
        updatedAt: new Date(),
    };
}

function getReasonOptions() {
    return (appData.config?.recalls?.recallReasons || [])
        .filter((option) => option?.id && option?.isActive !== false);
}

function getTextInputHtmlForValue({
    name,
    placeholder,
    type = "text",
    value = "",
}) {
    return `
        <input
            type="${attrValue(type)}"
            name="${attrValue(name)}"
            value="${attrValue(value)}"
            placeholder="${attrValue(placeholder)}"
            class="${NATIVE_INPUT_CLASSES}"
            data-recalls-entry-input
        >
    `;
}

function getReasonSelectHtml(item = {}) {
    return `
        <div data-recalls-reason-control></div>
        <input type="hidden" name="recall_reason" value="${attrValue(serializeRecallReasonIds(item.recall_reason))}" data-recalls-reason-mirror data-recalls-entry-input>
    `;
}

function getProviderSelectHtml(item = {}) {
    const providers = Array.isArray(item.provider) && item.provider.length > 0 ?
        item.provider :
        getCurrentUserRecallProviderDefault(appData, state);
    const selectedIds = getRecallProviderControlValues(providers);
    return `
        <div data-recalls-provider-control></div>
        <input type="hidden" name="provider" value="${attrValue(JSON.stringify(selectedIds))}" data-recalls-provider-mirror data-recalls-entry-input>
    `;
}

function getDateControlHtml(item = {}) {
    return `
        <div data-recalls-date-control></div>
        <input type="hidden" name="recall_date" value="${attrValue(item.recall_date)}" data-recalls-date-mirror data-recalls-entry-input>
    `;
}

function getNotesHtml(item = {}) {
    return `
        <textarea
            name="notes"
            rows="3"
            placeholder="Notes"
            class="${FIXED_TEXTAREA_CLASSES}"
            data-recalls-entry-input
        >${escapeHTML(item.notes || "")}</textarea>
    `;
}

function getQuickAddShellHtml(item = {}) {
    return `
        <div class="${RECALLS_MODAL_GRID_CLASSES} ${RECALLS_MODAL_BODY_SPACING_CLASSES}" data-recalls-quick-add-fields>
            ${getNativeFieldWrapperHtml("First Name", getTextInputHtmlForValue({
                name: "first_name",
                placeholder: "First name",
                value: item.first_name,
            }))}
            ${getNativeFieldWrapperHtml("Last Name", getTextInputHtmlForValue({
                name: "last_name",
                placeholder: "Last name",
                value: item.last_name,
            }))}
            ${getNativeFieldWrapperHtml("Email", getTextInputHtmlForValue({
                name: "email",
                placeholder: "patient@example.com",
                type: "email",
                value: item.email,
            }))}
            ${getNativeFieldWrapperHtml("Phone", getTextInputHtmlForValue({
                name: "phone",
                placeholder: "Phone",
                type: "tel",
                value: item.phone,
            }))}
            ${getNativeFieldWrapperHtml("Reason", getReasonSelectHtml(item), {
                className: "",
            })}
            ${getNativeFieldWrapperHtml("Provider", getProviderSelectHtml(item), {
                className: "",
            })}
            ${getNativeFieldWrapperHtml("Recall Date", getDateControlHtml(item))}
            ${getNativeFieldWrapperHtml("Notes", getNotesHtml(item), {
                className: "md:col-span-2",
            })}
        </div>
    `;
}

function getReasonControlOptions() {
    return getReasonOptions().map((option) => ({
        value: option.id,
        label: option.name || option.id,
        source: option,
    }));
}

function mountReasonControl(itemForm) {
    const root = itemForm.querySelector("[data-recalls-reason-control]");
    const mirrorInput = itemForm.querySelector("[data-recalls-reason-mirror]");
    if (!root || !mirrorInput) {return null;}

    return createSelectControl({
        root,
        mirrorInput,
        name: "recall_reason",
        options: getReasonControlOptions(),
        placeholder: "+ Add reason",
        value: normalizeRecallReasonIds(mirrorInput.value),
        multiple: true,
        clearable: true,
    });
}

function mountProviderControl(itemForm, item = {}) {
    const root = itemForm.querySelector("[data-recalls-provider-control]");
    const mirrorInput = itemForm.querySelector("[data-recalls-provider-mirror]");
    if (!root || !mirrorInput) {return null;}
    const selectedProviders = Array.isArray(item.provider) && item.provider.length > 0 ?
        item.provider :
        getCurrentUserRecallProviderDefault(appData, state);

    const providerControl = createRecallProviderSelectPreset({
        root,
        appData,
        mirrorInput,
        name: "provider",
        selectedProviders,
        value: parseRecallProviderMirrorValue(mirrorInput.value),
        searchable: true,
    });
    return providerControl;
}

function mountNotesTagging(itemForm) {
    const notesInput = itemForm.querySelector("textarea[name=\"notes\"]");
    if (!notesInput) {return;}
    void initTagging(notesInput, "new-recall", "recalls");
}

function mountDateControl(itemForm) {
    const root = itemForm.querySelector("[data-recalls-date-control]");
    const mirrorInput = itemForm.querySelector("[data-recalls-date-mirror]");
    if (!root || !mirrorInput) {return null;}

    return createDateControl({
        root,
        mirrorInput,
        name: "recall_date",
        type: "date",
        value: mirrorInput.value || "",
    });
}

const QUICK_ADD_SECONDARY_BUTTON_CLASSES =
    "bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-3 rounded-lg sm:px-4";
const QUICK_ADD_PRIMARY_BUTTON_CLASSES =
    "rounded-lg bg-[var(--dfc-brand-primary)] px-3 py-2 font-bold text-[var(--dfc-brand-primary-contrast)] hover:bg-[var(--dfc-brand-primary-hover)] sm:px-4";
const QUICK_ADD_BLOCKED_BUTTON_CLASSES =
    "rounded-lg bg-gray-300 px-3 py-2 font-bold text-gray-500 cursor-not-allowed sm:px-4";

function configureQuickAddActions(itemForm) {
    const cancelBtn = document.getElementById("cancel-form-btn");
    const submitBtn = itemForm.querySelector("button[type=\"submit\"]");
    const footer = cancelBtn?.parentElement;
    if (!cancelBtn || !submitBtn || !footer) {return;}

    footer.className =
        "mt-3 flex flex-nowrap items-center justify-between gap-2 px-2 shrink-0";
    cancelBtn.className = QUICK_ADD_SECONDARY_BUTTON_CLASSES;

    let actionGroup = document.getElementById("recall-quick-add-action-group");
    if (!actionGroup) {
        actionGroup = document.createElement("div");
        actionGroup.id = "recall-quick-add-action-group";
    }
    actionGroup.dataset.modalCustomActions = "true";
    actionGroup.className =
        "ml-auto flex flex-nowrap items-center justify-end gap-2";
    actionGroup.innerHTML = "";

    const draftBtn = document.createElement("button");
    draftBtn.type = "submit";
    draftBtn.dataset.recallQuickAddAction = "draft";
    draftBtn.className = QUICK_ADD_SECONDARY_BUTTON_CLASSES;
    draftBtn.textContent = "Save draft";

    submitBtn.dataset.recallQuickAddAction = "save";
    submitBtn.textContent = "Save";

    actionGroup.append(draftBtn, submitBtn);
    footer.append(cancelBtn, actionGroup);
}

function syncSubmitState(itemForm) {
    const submitBtn = itemForm.querySelector("[data-recall-quick-add-action=\"save\"]");
    const fieldsRoot = itemForm.querySelector("[data-recalls-quick-add-fields]");
    if (!submitBtn || !fieldsRoot) {return;}
    const saveReady = isQuickAddSaveReady(collectRecallEntryData(fieldsRoot));
    submitBtn.disabled = !saveReady;
    submitBtn.className = saveReady ?
        QUICK_ADD_PRIMARY_BUTTON_CLASSES :
        QUICK_ADD_BLOCKED_BUTTON_CLASSES;
}

/**
 * Opens the Recalls quick-add modal shell.
 * @returns {void}
 */
export function openRecallQuickAddModal(options = {}) {
    openStandardForm("recall", null, {
        getFieldsHtml: getQuickAddShellHtml,
        afterRender({ itemForm }) {
            itemForm.noValidate = true;
            configureQuickAddActions(itemForm);
            bindNativeFieldBehavior(itemForm);
            bindRecallPhoneInputSanitizer(itemForm);
            mountReasonControl(itemForm);
            mountProviderControl(itemForm, {});
            mountDateControl(itemForm);
            mountNotesTagging(itemForm);
            syncSubmitState(itemForm);
            itemForm.addEventListener("input", () => syncSubmitState(itemForm));
            itemForm.addEventListener("change", () => syncSubmitState(itemForm));
            itemForm.onsubmit = async (event) => {
                event.preventDefault();
                const actionBtn = event.submitter || itemForm.querySelector("button[type=\"submit\"]");
                const mode = actionBtn?.dataset.recallQuickAddAction === "draft" ?
                    "draft" :
                    "save";
                const fieldsRoot = itemForm.querySelector("[data-recalls-quick-add-fields]");
                if (!fieldsRoot) {return;}
                const data = collectRecallEntryData(fieldsRoot);
                if (mode === "draft") {
                    saveNewDraftOptimistically(data, options);
                    return;
                }
                try {
                    setRecallsModalActionProcessing(actionBtn, true);
                    await saveRecallQuickAdd(
                        data,
                        () => {
                            closeForm();
                            void refreshRecallsAfterPermanentSave({
                                onData: options.onSuccess,
                                recall: data,
                            });
                        },
                        { mode },
                    );
                } catch (error) {
                    if (openRecallsConfigIncompleteModal(error)) {return;}
                    console.error("Error saving Recall quick add:", error);
                } finally {
                    if (actionBtn) {
                        setRecallsModalActionProcessing(actionBtn, false);
                        syncSubmitState(itemForm);
                    }
                }
            };
        },
    });
}

function saveNewDraftOptimistically(data, options = {}) {
    const optimisticDraft = buildOptimisticDraft(data);
    addOptimisticRecallDraft(optimisticDraft);
    closeForm();
    options.onSuccess?.();

    saveRecallDraft({ data })
        .then((response) => {
            replaceOptimisticRecallDraft(optimisticDraft.id, {
                ...optimisticDraft,
                id: response?.draftId || optimisticDraft.id,
                updatedAt: response?.updatedAt || optimisticDraft.updatedAt,
            });
            options.onSuccess?.();
        })
        .catch((error) => {
            if (openRecallsConfigIncompleteModal(error)) {return;}
            console.error("Error saving Recall draft:", error);
            removeCachedRecallDraft(optimisticDraft.id);
            options.onSuccess?.();
            showErrorModal(
                "Save Recall Draft",
                error?.message || "The Recall draft could not be saved.",
            );
        });
}

async function saveEditedDraftData({ data, draft }) {
    return saveRecallDraft({
        data,
        draftId: draft.id,
        expectedUpdatedAt: draft.updatedAt || null,
    });
}

function saveEditedDraftOptimistically({ data, draft, onSuccess }) {
    const previousDraft = { ...draft };
    const optimisticDraft = buildOptimisticDraft(data, draft);
    upsertCachedRecallDraft(optimisticDraft);
    closeForm();
    onSuccess?.();

    saveEditedDraftData({ data, draft })
        .then((response) => {
            upsertCachedRecallDraft({
                ...optimisticDraft,
                updatedAt: response?.updatedAt || optimisticDraft.updatedAt,
            });
            onSuccess?.();
        })
        .catch((error) => {
            console.error("Error saving Recall draft:", error);
            upsertCachedRecallDraft(previousDraft);
            onSuccess?.();
            showErrorModal(
                "Save Recall Draft",
                error?.message || "The Recall draft could not be saved.",
            );
        });
}

async function promoteEditedDraft({ data, draft, onSuccess }) {
    const expectedUpdatedAt = draft.updatedAt || null;
    const promotedDraft = {
        ...draft,
        ...data,
    };
    const action = async (extra = {}) => promoteRecallDraft({
        data,
        draftId: draft.id,
        expectedUpdatedAt,
        ...extra,
    });
    const response = await action();
    await handleRecallWarningResponse({
        action,
        data: promotedDraft,
        onSuccess,
        response,
    });
}

/**
 * Opens the Recall draft edit modal.
 * @param {object} draft Shared Recall draft row.
 * @param {{ onSuccess?: Function }} [options={}] Modal options.
 * @returns {void}
 */
export function openRecallDraftEditModal(draft, options = {}) {
    if (!draft?.id) {return;}

    openStandardForm("recall_draft", draft, {
        getFieldsHtml: getQuickAddShellHtml,
        afterRender({ itemForm }) {
            itemForm.noValidate = true;
            configureQuickAddActions(itemForm);
            bindNativeFieldBehavior(itemForm);
            bindRecallPhoneInputSanitizer(itemForm);
            mountReasonControl(itemForm);
            mountProviderControl(itemForm, draft);
            mountDateControl(itemForm);
            mountNotesTagging(itemForm);
            syncSubmitState(itemForm);
            itemForm.addEventListener("input", () => syncSubmitState(itemForm));
            itemForm.addEventListener("change", () => syncSubmitState(itemForm));
            itemForm.onsubmit = async (event) => {
                event.preventDefault();
                const actionBtn = event.submitter || itemForm.querySelector("button[type=\"submit\"]");
                const mode = actionBtn?.dataset.recallQuickAddAction === "draft" ?
                    "draft" :
                    "save";
                const fieldsRoot = itemForm.querySelector("[data-recalls-quick-add-fields]");
                if (!fieldsRoot || (mode === "save" && actionBtn?.disabled)) {
                    return;
                }
                const data = collectRecallEntryData(fieldsRoot);
                try {
                    setRecallsModalActionProcessing(actionBtn, true);
                    if (mode === "draft") {
                        saveEditedDraftOptimistically({
                            data,
                            draft,
                            onSuccess: options.onSuccess,
                        });
                        return;
                    }
                    await confirmNearDateIfNeeded(data, async () => {
                        await promoteEditedDraft({
                            data,
                            draft,
                            onSuccess: (result) => {
                                closeForm();
                                void refreshRecallsAfterDraftPromotion({
                                    draft: {
                                        ...draft,
                                        ...data,
                                    },
                                    onData: options.onSuccess,
                                    response: result,
                                });
                            },
                        });
                    });
                } catch (error) {
                    if (openRecallsConfigIncompleteModal(error)) {return;}
                    console.error("Error saving Recall draft:", error);
                    showErrorModal(
                        "Save Recall Draft",
                        error?.message || "The Recall draft could not be saved.",
                    );
                } finally {
                    if (actionBtn) {
                        setRecallsModalActionProcessing(actionBtn, false);
                        syncSubmitState(itemForm);
                    }
                }
            };
        },
    });
}

/**
 * Binds Recall draft table rows to edit and delete actions.
 * @param {HTMLElement} container Recalls route container.
 * @param {Function} rerender Route rerender callback.
 * @returns {void}
 */
export function bindRecallDraftTableActions(container, rerender) {
    container.querySelectorAll("[data-recall-draft-id]").forEach((row) => {
        const draftId = row.dataset.recallDraftId;
        const draft = (appData.recallDrafts || [])
            .find((item) => item.id === draftId);
        if (!draft) {return;}

        row.addEventListener("click", (event) => {
            if (event.target.closest("[data-recalls-draft-delete]")) {return;}
            openRecallDraftEditModal(draft, { onSuccess: rerender });
        });
    });

    container.querySelectorAll("[data-recalls-draft-delete]").forEach((button) => {
        button.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            const draftId = button.dataset.recallsDraftDelete;
            const draft = (appData.recallDrafts || [])
                .find((item) => item.id === draftId);
            if (!draft) {return;}

            openConfirmationModal(
                "Delete Recall Draft",
                "Delete this shared Recall draft?",
                () => {
                    removeCachedRecallDraft(draft.id);
                    rerender();
                    deleteRecallDraft({
                        draftId: draft.id,
                        expectedUpdatedAt: draft.updatedAt || null,
                    }).catch((error) => {
                        console.error("Error deleting Recall draft:", error);
                        upsertCachedRecallDraft(draft);
                        rerender();
                        showErrorModal(
                            "Delete Recall Draft",
                            error?.message ||
                                "The Recall draft could not be deleted.",
                        );
                    });
                    return true;
                },
                true,
                "Delete",
            );
        });
    });
}
